import React, { useCallback, useEffect, useState } from 'react';
import { DocumentDuplicateIcon, TruckIcon } from '@heroicons/react/24/outline';
import { travelAPI } from '../../services/api';
import type { EventTravel } from '../../types/travel';
import TravelPlanView from './TravelPlanView';
import TravelEditorModal from './TravelEditorModal';
import TravelRespondPanel from './TravelRespondPanel';
import TravelCopyModal from './TravelCopyModal';

interface TravelSectionProps {
  eventId: number;
  /** Változásakor újratölt (pl. "Nem jön" után a játékos kikerül az utazásból). */
  refreshKey?: number;
  /** A doboz stílusa (a családi nézet fehér kártyát kér). */
  className?: string;
  /** Családi nézet: a cím a lapon kívül van, az úti cél és a módosító neve nem kell. */
  compact?: boolean;
  /** A doboz fölé kerül, de csak ha az utazás egyáltalán látszik. */
  heading?: React.ReactNode;
  /**
   * A szülő jelzései (hogyan jut el, saját autó) a visszajelzés-kártyán vannak,
   * itt csak a megszervezett utazás látszik.
   */
  respondElsewhere?: boolean;
  /** Az utazás itt megváltozott (pl. autó felajánlása). */
  onChanged?: () => void;
}

/**
 * Az esemény utazása a részletek között. A szülő és a játékos itt jelzi, hogy
 * fuvar kell, maga megy, vagy melyik megállóban száll fel, és felajánlhatja az
 * autóját; a stáb innen szervezi és másolja át más eseményekre. Akinek egyik
 * sem jár, annak csak akkor jelenik meg, ha már megszervezték.
 */
const TravelSection: React.FC<TravelSectionProps> = ({
  eventId,
  refreshKey = 0,
  className = 'mb-6 rounded-lg bg-gray-50 p-4',
  compact = false,
  heading,
  respondElsewhere = false,
  onChanged,
}) => {
  const [travel, setTravel] = useState<EventTravel | null>(null);
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [copying, setCopying] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await travelAPI.getTravel(eventId);
      setTravel(loaded);
      return loaded;
    } catch (err: any) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        setHidden(true);
      } else {
        console.error('Az utazás betöltése nem sikerült:', err);
      }
      return null;
    }
  }, [eventId]);

  useEffect(() => {
    setHidden(false);
    setTravel(null);
    setEditing(false);
    load();
  }, [load]);

  useEffect(() => {
    if (refreshKey > 0) load();
  }, [refreshKey, load]);

  if (hidden || !travel) return null;

  const hasContent = travel.vehicles.length > 0 || travel.passengers.length > 0 || !!travel.notes;
  const hasStaffVehicles = travel.vehicles.some((v) => !v.isOffered);

  // A jelzés a visszajelzés-kártyán van: amíg nincs mit mutatni, a doboz sem kell
  if (respondElsewhere && !travel.canEdit && !(travel.exists && hasContent)) return null;

  return (
    <>
    {heading}
    <div className={className}>
      <div className={`mb-3 flex flex-wrap items-center justify-between gap-2 ${compact && !travel.canEdit ? 'hidden' : ''}`}>
        <div className="flex items-center gap-2">
          <TruckIcon className="h-5 w-5 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">Utazás</h4>
        </div>
        {travel.canEdit && (
          <div className="flex flex-wrap gap-2">
            {hasStaffVehicles && (
              <button
                type="button"
                onClick={() => setCopying(true)}
                className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                Másolás más eseményekre
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
            >
              {travel.exists ? 'Utazás szerkesztése' : 'Utazás szervezése'}
            </button>
          </div>
        )}
      </div>

      {travel.canRespond && !respondElsewhere && (
        <div className="mb-3">
          <TravelRespondPanel
            travel={travel}
            onChange={(updated) => {
              setTravel(updated);
              onChanged?.();
            }}
          />
        </div>
      )}

      {travel.exists && hasContent ? (
        <TravelPlanView travel={travel} showUnassigned={travel.canEdit} hideMine={travel.canRespond} compact={compact} />
      ) : (
        !travel.canRespond && (
          <p className="text-sm text-gray-500">Még nincs megszervezve, ki melyik járművel, honnan utazik.</p>
        )
      )}

      {editing && (
        <TravelEditorModal
          key={editorKey}
          travel={travel}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setTravel(saved);
            setEditing(false);
          }}
          onDeleted={() => {
            setEditing(false);
            load();
          }}
          onReload={async () => {
            // Közben valaki módosította: a friss állapotból nyitjuk újra
            if (await load()) setEditorKey((k) => k + 1);
          }}
        />
      )}

      {copying && <TravelCopyModal eventId={travel.eventId} eventTitle={travel.eventTitle} onClose={() => setCopying(false)} />}
    </div>
    </>
  );
};

export default TravelSection;
